var RepeaterGridArranger = require("enyo-ui/RepeaterGridArranger"),
    kind                 = require("enyo/kind");

require("enyo-ui");

/**
 * [RepeaterPaddedGridArranger]{@link enyo-ui.RepeaterPaddedGridArranger} is an
 * extension of [RepeaterGridArranger]{@link enyo-ui.RepeaterGridArranger}
 * implementing the ability to configure a spacing between the items and a
 * padding around the grid.
 *
 * Grid items are expected to implement [FitableSupport]{@link
 * enyo-ui.FitableSupport}, to allow easy calculation of their dimensions.
 *
 * @ui
 * @class   enyo-ui.RepeaterPaddedGridArranger
 * @extends enyo-ui.RepeaterGridArranger
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.RepeaterPaddedGridArranger.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.RepeaterPaddedGridArranger",

    /**
     * @private
     */
    kind: RepeaterGridArranger,

    /**
     * The width of a column in the grid. The width of a column can be thought
     * of as the sum of one cellWidth ánd one colSpacing.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    colWidth: 0,

    /**
     * The height of a row in the grid. The height of a column can be thought of
     * as the sum of one cellHeight ánd one rowSpacing.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    rowHeight: 0,

    /**
     * @private
     */
    options: /** @lends  enyo-ui.RepeaterGridArranger.prototype */ {

        /**
         * The amount of spacing between columns.
         *
         * @type {number}
         * @default 0
         * @public
         */
        colSpacing: 0,

        /**
         * The amount of spacing between rows.
         *
         * @type {number}
         * @default 0
         * @public
         */
        rowSpacing: 0,

        /**
         * The amount of padding the top of the grid.
         *
         * @return {number}  The padding
         * @public
         */
        padTop: function() {
            return this.getOption("rowSpacing");
        },

        /**
         * The amount of padding the bottom of the grid.
         *
         * @return {number}  The padding
         * @public
         */
        padBottom: function() {
            return this.getOption("rowSpacing");
        },

        /**
         * The amount of padding the left of the grid.
         *
         * @return {number}  The padding
         * @public
         */
        padLeft: function() {
            return this.getOption("colSpacing");
        },

        /**
         * The amount of padding the right of the grid.
         *
         * @return {number}  The padding
         * @public
         */
        padRight: function() {
            return this.getOption("colSpacing");
        }
    },

    /**
     * Calculate the width of a column.
     *
     * @private
     */
    calcColWidth: function() {
        var colSpacing,
            rowSpacing,
            viewportWidth,
            padLeft,
            padRight,
            maxColWidth,
            step,
            offset,
            availableWidth,
            colWidth,
            cols,
            rowHeight,
            childHeight,
            childWidth;

        colSpacing = this.getOption("colSpacing");

        switch (this.getOption("dividingAxis") || "x") {
            case "y":
                rowHeight  = this.rowHeight;
                rowSpacing = this.getOption("rowSpacing");

                childHeight = rowHeight - rowSpacing;
                childWidth  = Math.max(0, this.layoutChild.calcWidth(childHeight));
                colWidth    = childWidth > 0 ? childWidth + colSpacing : 0;
                break;
            case "x":
            default:
                viewportWidth = this.getOption("viewportWidth");
                padLeft       = this.getOption("padLeft");
                padRight      = this.getOption("padRight");
                maxColWidth   = this.getOption("maxColWidth");
                step          = this.getOption("divisionStep");
                offset        = this.getOption("divisionOffset");

                availableWidth = viewportWidth
                    + colSpacing // One colSpacing worth of overflow
                    - padLeft // Compensate for left padding
                    - padRight; // Compensate for right padding

                if (availableWidth <= 0 || maxColWidth <= 0 || step <= 0) {
                    colWidth = 0;
                }
                else {
                    offset   = offset < 0 ? 0 : offset;
                    step     = step <= 0 ? 1 : step;
                    cols     = availableWidth / maxColWidth;
                    cols     = Math.ceil(cols / step) * step;
                    colWidth = availableWidth / (cols + offset);
                }
                break;
        }

        this.colWidth = colWidth;
    },

    /**
     * Calculate the height of a row.
     *
     * @private
     */
    calcRowHeight: function() {
        var colSpacing,
            rowSpacing,
            colWidth,
            childWidth,
            childHeight,
            rowHeight,
            viewportHeight,
            padTop,
            padBottom,
            maxRowHeight,
            step,
            offset,
            availableHeight,
            rows;

        rowSpacing = this.getOption("rowSpacing");

        switch (this.getOption("dividingAxis") || "x") {
            case "y":
                viewportHeight = this.getOption("viewportHeight");
                padTop         = this.getOption("padTop");
                padBottom      = this.getOption("padBottom");
                maxRowHeight   = this.getOption("maxRowHeight");
                step           = this.getOption("divisionStep");
                offset         = this.getOption("divisionOffset");

                availableHeight = viewportHeight
                    + rowSpacing // One rowSpacing worth of overflow
                    - padTop // Compensate for top padding
                    - padBottom; // Compensate for bottom padding

                if (availableHeight <= 0 || maxRowHeight <= 0 || step <= 0) {
                    rowHeight = 0;
                }
                else {
                    offset    = offset < 0 ? 0 : offset;
                    step      = step <= 0 ? 1 : step;
                    rows      = availableHeight / maxRowHeight;
                    rows      = Math.ceil(rows / step) * step;
                    rowHeight = availableHeight /  (rows + offset);
                }
                break;
            case "x":
            default:
                colWidth   = this.colWidth;
                colSpacing = this.getOption("colSpacing");

                childWidth  = colWidth - colSpacing;
                childHeight = Math.max(0, this.layoutChild.calcHeight(childWidth));
                rowHeight   = childHeight > 0 ? childHeight + rowSpacing : 0;
                break;
        }

        this.rowHeight = rowHeight;
    },

    /**
     * Calculates how many cols will fit within the `maxGridWidth` and `maxCols`
     * boundaries.
     *
     * The grid will not be allowed to expand beyond this amount of columns.
     *
     * @private
     */
    calcMaxCols: function() {
        var maxCols,
            colWidth,
            maxGridWidth,
            colSpacing,
            padLeft,
            padRight,
            availableWidth;

        colWidth     = this.colWidth;
        maxGridWidth = this.getOption("maxGridWidth");
        colSpacing   = this.getOption("colSpacing");
        padLeft      = this.getOption("padLeft");
        padRight     = this.getOption("padRight");
        maxCols      = this.getOption("maxCols");

        availableWidth = maxGridWidth
            + colSpacing // One colSpacing worth of overflow
            - padLeft // Compensate for left padding
            - padRight; // Compensate for right padding

        if (colWidth === 0 || availableWidth <= 0 || maxCols <= 0) {
            maxCols = 0;
        }
        else {
            // Adding a tiny amount to the amount of columns that fit within the
            // available width prevents issues with floating-point precision.
            maxCols = Math.min(Math.floor(availableWidth / colWidth + 0.000001), maxCols);
        }

        this.maxCols = maxCols;
    },

    /**
     * Calculates how many cols will fit within the `maxGridHeight` and
     * `maxRows` boundaries.
     *
     * The grid will not be allowed to expand beyond this amount of rows.
     *
     * @private
     */
    calcMaxRows: function() {
        var rowHeight,
            maxGridHeight,
            rowSpacing,
            padTop,
            padBottom,
            maxRows,
            availableHeight;

        rowHeight     = this.rowHeight;
        maxGridHeight = this.getOption("maxGridHeight");
        rowSpacing    = this.getOption("rowSpacing");
        padTop        = this.getOption("padTop");
        padBottom     = this.getOption("padBottom");
        maxRows       = this.getOption("maxRows");

        availableHeight = maxGridHeight
            + rowSpacing // One rowSpacing worth of overflow
            - padTop // Compensate for top padding
            - padBottom; // Compensate for bottom padding

        if (rowHeight === 0 || availableHeight <= 0 || maxRows <= 0) {
            maxRows = 0;
        }
        else {
            // Adding a tiny amount to the amount of columns that fit within the
            // available width prevents issues with floating-point precision.
            maxRows = Math.min(Math.floor(availableHeight / rowHeight + 0.000001), maxRows);
        }

        this.maxRows = maxRows;
    },

    /**
     * Get the size of the entire arrangement.
     *
     * @return {Object}  The arrangement size.
     * @public
     */
    getArrangementSize: function() {
        var cols        = this.getCols(),
            rows        = this.getRows(),
            roundPixels = this.getOption("roundPixels"),
            colSpacing  = this.getOption("colSpacing"),
            rowSpacing  = this.getOption("rowSpacing"),
            padLeft     = this.getOption("padLeft"),
            padRight    = this.getOption("padRight"),
            padTop      = this.getOption("padTop"),
            padBottom   = this.getOption("padBottom"),
            bounds      = {};

        if (cols === 0 || rows === 0) {
            return {
                width:  0,
                height: 0
            };
        }

        bounds.width  = padLeft + cols * this.colWidth - colSpacing + padRight;
        bounds.height = padTop + rows * this.rowHeight - rowSpacing + padBottom;

        if (roundPixels) {
            bounds.width  = Math.floor(bounds.width);
            bounds.height = Math.floor(bounds.height);
        }

        return bounds;
    },

    /**
     * Gets a sorted set of indexes of children within the arrangement such that
     * either the child itself ór half of the colSpacing/rowSpacing around it
     * intersect with the provided bounds.
     *
     * The incoming bounds should be defined in pixels, relative to the top-left
     * corner of the childContainer.
     *
     * @param  {Object}  bounds         The bounds
     * @param  {number}  bounds.top     The top edge of the bounding box in
     *                                  pixels from the top of the grid
     * @param  {number}  bounds.left    The left edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.right   The right edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.bottom  The bottom edge of the bounding box in
     *                                  pixels from the top of the grid
     * @return {Array}   Array of indexes
     * @method
     * @public
     */
    getArrangementIndexes: kind.inherit(function(sup) {
        return function(bounds) {
            var padLeft    = this.getOption("padLeft"),
                padTop     = this.getOption("padTop"),
                colSpacing = this.getOption("colSpacing"),
                rowSpacing = this.getOption("rowSpacing");

            // Offset the bounds to simulate a grid with no padding and with
            // children centered within their rows/cols
            bounds.left   = bounds.left - padLeft + (colSpacing / 2);
            bounds.right  = bounds.right - padLeft + (colSpacing / 2);
            bounds.top    = bounds.top - padTop + (rowSpacing / 2);
            bounds.bottom = bounds.bottom - padTop + (rowSpacing / 2);

            // Super method can now handle the rest
            return sup.call(this, bounds);
        };
    }),

    /**
     * Returns the distance (in pixels) between the top edge of the child
     * control at the given index, and the top edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @method
     * @public
     */
    getOffsetTop: function(index) {
        var row         = this.getRowByIndex(index) || 0,
            offset,
            padTop      = this.getOption("padTop"),
            roundPixels = this.getOption("roundPixels");

        offset = row > 1 ? (row - 1) * this.rowHeight + padTop : padTop;

        return roundPixels ? Math.floor(offset) : offset;
    },

    /**
     * Returns the distance (in pixels) between the left edge of the child
     * control at the given index, and the left edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @method
     * @public
     */
    getOffsetLeft: function(index) {
        var col         = this.getColByIndex(index) || 0,
            offset,
            padLeft     = this.getOption("padLeft"),
            roundPixels = this.getOption("roundPixels");

        offset = col > 1 ? (col - 1) * this.colWidth + padLeft : padLeft;

        return roundPixels ? Math.floor(offset) : offset;
    },

    /**
     * Gets the index of the child positioned closest to the provided
     * coordinates. If the provided coordinates are located in the spacing
     * between cells, the index positioned to the bottom-right is returned.
     *
     * The incoming coordinates should be defined in pixels, relative to the
     * top-left corner of the childContainer.
     *
     * @param  {number}  x  The coordinates on the `x` axis.
     * @param  {number}  y  The coordinates on the `y` axis.
     * @return {number}  The index
     * @method
     * @public
     */
    getNearestIndex: kind.inherit(function(sup) {
        return function(x, y) {
            var colSpacing = this.getOption("colSpacing"),
                rowSpacing = this.getOption("rowSpacing"),
                padLeft    = this.getOption("padLeft"),
                padTop     = this.getOption("padTop");

            // Offset the coordinates to simulate a grid with no padding and
            // with children positioned in the bottom-right corner of their
            // cell.
            x = x - padLeft + colSpacing;
            y = y - padTop + rowSpacing;

            // Super method can now handle the rest
            return sup.call(this, x, y);
        };
    }),

    /**
     * Returns the scroll offset (in pixels) that would align the top edge of
     * the child arranged at the provided index exactly one `rowSpacing` below
     * the top edge of a scrolled view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollTop: function(index) {
        var row    = this.getRowByIndex(index) || 0,
            offset = this.getOffsetTop(index);

        return offset - this.getOption(row === 1 ? "padTop" : "rowSpacing");
    },

    /**
     * Returns the scroll offset (in pixels) that would align the left edge of
     * the child arranged at the provided index exactly one `colSpacing` to the
     * right of the left edge of a scrolled view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollLeft: function(index) {
        var col    = this.getColByIndex(index) || 0,
            offset = this.getOffsetLeft(index);

        return offset - this.getOption(col === 1 ? "padLeft" : "colSpacing");
    },

    /**
     * All children have the same size.
     *
     * @return {Object}  The size at index.
     * @protected
     */
    getSizeAtIndex: function() {
        var roundPixels = this.getOption("roundPixels"),
            colWidth    = this.colWidth,
            colSpacing  = this.getOption("colSpacing"),
            rowHeight   = this.rowHeight,
            rowSpacing  = this.getOption("rowSpacing");

        return {
            width:  roundPixels ? Math.floor(colWidth - colSpacing) : colWidth - colSpacing,
            height: roundPixels ? Math.floor(rowHeight - rowSpacing) : rowHeight - rowSpacing
        };
    },

    /**
     * Renders the child if needed and sizes it.
     *
     * @param  {enyo.Control}  child  The child
     * @public
     */
    drawChild: function(child) {
        var childContainer = this.repeater.getChildContainer(),
            rendered       = child.hasNode(),
            roundPixels    = this.getOption("roundPixels"),
            colWidth       = this.colWidth,
            rowHeight      = this.rowHeight,
            colSpacing     = this.getOption("colSpacing"),
            rowSpacing     = this.getOption("rowSpacing"),
            childWidth     = colWidth - colSpacing,
            childHeight    = rowHeight - rowSpacing,
            childBounds;

        // Determine the child's bounds
        childBounds = {
            width:  roundPixels ? Math.floor(childWidth) : childWidth,
            height: roundPixels ? Math.floor(childHeight) : childHeight
        };

        // Size child
        child.setBounds(childBounds);

        // For use internal to the arranger.
        child.set("repeaterSize", {
            width:  roundPixels ? Math.floor(colWidth) : colWidth,
            height: roundPixels ? Math.floor(rowHeight) : rowHeight
        });

        // For use outside of the arranger.
        child.set("repeaterBounds", childBounds);

        // Render child as needed.
        if (childContainer.hasNode() && !rendered) {
            child.canGenerate = true;
            child.addStyles({
                position: "absolute",
                top:      0,
                left:     0
            });
            child.render();
        }
    }

});
